import { z } from 'zod'
import { DistanceModel, Directivity, Fade, SoundSource } from './audio.js'
import { GainDb, Id, Metres, MetresPerSecond, Milliseconds, Vec2 } from './primitives.js'

const emitterBase = {
  id: Id,
  name: z.string().max(120),
  source: SoundSource,
  busId: Id,
  gainDb: GainDb.default(0),
  loop: z.boolean().default(false),
  /** Equal-power crossfade across the loop seam, so ambience beds never click. */
  loopCrossfadeMs: Milliseconds.default(250),
  /** Higher survives voice culling. Narration sits above ambience by default. */
  priority: z.number().int().min(0).max(100).default(50),
  startBehavior: z.enum(['sessionStart', 'rule']).default('rule'),
  fadeIn: Fade.default({ durationMs: 0, curve: 'equalPower' }),
  fadeOut: Fade.default({ durationMs: 0, curve: 'equalPower' }),
  /** Sponsored audio must be declared, disclosed to listeners and frequency-capped. */
  isAd: z.boolean().default(false),
  colorHex: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
}

/** A sound at a fixed spot in the world. */
export const PointEmitter = z.object({
  kind: z.literal('point'),
  ...emitterBase,
  position: Vec2,
  elevationM: z.number().finite().default(0),
  distance: DistanceModel.default({}),
  directivity: Directivity.optional(),
})

/**
 * An ambient layer filling a region. Outside it is spatialised toward the
 * region's centroid; crossing the boundary it fades over `transitionWidthM`
 * into enveloping playback, so walking into a courtyard feels like arriving
 * rather than like a switch being thrown.
 */
export const ZoneEmitter = z.object({
  kind: z.literal('zone'),
  ...emitterBase,
  regionId: Id,
  transitionWidthM: Metres.default(8),
  distance: DistanceModel.default({}),
})

/** A sound travelling a route — the helicopter circling the site. */
export const PathEmitter = z.object({
  kind: z.literal('path'),
  ...emitterBase,
  waypoints: z.array(Vec2).min(2),
  motion: z
    .discriminatedUnion('mode', [
      z.object({ mode: z.literal('constantSpeed'), speedMps: MetresPerSecond }),
      z.object({ mode: z.literal('duration'), durationMs: Milliseconds }),
    ])
    .default({ mode: 'constantSpeed', speedMps: 12 }),
  traversal: z.enum(['once', 'loop', 'pingPong']).default('once'),
  /** Pitch shift from closing speed, clamped by the engine to stay musical. */
  doppler: z.boolean().default(true),
  distance: DistanceModel.default({}),
})

/** A global, non-diegetic layer: the music bed, the base ambience. */
export const BedEmitter = z.object({
  kind: z.literal('bed'),
  ...emitterBase,
})

/**
 * A sound attached to the listener at a body-relative offset — a coach at your
 * shoulder, footsteps two metres behind. Offsets are in the listener's frame,
 * so they rotate as the listener turns.
 */
export const CompanionEmitter = z.object({
  kind: z.literal('companion'),
  ...emitterBase,
  offset: z.object({
    forwardM: z.number().finite().default(0),
    rightM: z.number().finite().default(0),
    upM: z.number().finite().default(0),
  }),
  distance: DistanceModel.default({}),
})

export const Emitter = z.discriminatedUnion('kind', [
  PointEmitter,
  ZoneEmitter,
  PathEmitter,
  BedEmitter,
  CompanionEmitter,
])
export type Emitter = z.infer<typeof Emitter>
export type EmitterKind = Emitter['kind']

/** Emitter kinds that are rendered positionally and therefore consume a spatial voice. */
export const POSITIONAL_EMITTER_KINDS = ['point', 'zone', 'path', 'companion'] as const
